import { RolesClient, RolesResponse, RolePermissionsResponse, UpdateUserRolesRequest, UpdateRolePermissionsRequest, CreateRoleRequest } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { useMutation, useQuery } from "@tanstack/react-query";

const rolesClient = new RolesClient(undefined, axios);

export const useRoles = (options?: any) => {
  return useQuery<RolesResponse>({
    ...options,
    queryKey: ["roles"],
    initialData: { roles: [] },
    queryFn: async () => rolesClient.getRoles(),
  });
};

export const useUpdateUserRoles = (userId: string, options?: any) => {
  return useMutation<void, unknown, UpdateUserRolesRequest>({
    ...options,
    mutationFn: (updateUserRolesRequest: UpdateUserRolesRequest) => rolesClient.updateUserRoles(userId, updateUserRolesRequest),
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: ["user", userId] });
      options?.onSuccess?.();
    },
  });
};

export const useRolePermissions = (roleId?: string, options?: any) => {
  return useQuery<RolePermissionsResponse>({
    ...options,
    queryKey: ["rolePermissions", roleId],
    enabled: !!roleId && (options?.enabled !== false),
    initialData: { roleId: roleId || "", roleName: "", permissions: [] },
    queryFn: async () => rolesClient.getRolePermissions(roleId!),
  });
};

export const useUpdateRolePermissions = (roleId: string, options?: any) => {
  return useMutation<void, unknown, UpdateRolePermissionsRequest>({
    ...options,
    mutationFn: (updateRolePermissionsRequest: UpdateRolePermissionsRequest) =>
      rolesClient.updateRolePermissions(roleId, updateRolePermissionsRequest),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rolePermissions", roleId] });
      options?.onSuccess?.();
    },
  });
};

export const useCreateRole = (options?: any) => {
  return useMutation<void, unknown, CreateRoleRequest>({
    ...options,
    mutationFn: (createRoleRequest: CreateRoleRequest) => rolesClient.createRole(createRoleRequest),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      options?.onError?.(error);
    },
  });
};

export const useDeleteRole = (options?: any) => {
  return useMutation<void, unknown, string>({
    ...options,
    mutationFn: (roleName: string) => rolesClient.deleteRole(roleName),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      options?.onError?.(error);
    },
  });
};